import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Clock, AlertCircle, Plus, Calendar, Sidebar, X } from 'lucide-react';
import type { Todo, Week, TaskStatus, TodoCreate, TodoUpdate } from '../types';
import { weeksService } from '../services/weeks';
import { eventsAPI, weeksAPI } from '../services/api';
import TodoItem from './TodoItem';
import WeekSummary from './WeekSummary';
import ReviewModal from './ReviewModal';
import { SearchFilterBar } from './SearchFilterBar';
import { useNotification } from '../hooks/useNotification';
import { parseLocalTimestamp } from '../utils/dateUtils';
import { getInboxTasks } from '../utils/inboxFilter';

interface WeekViewProps {
  selectedTeamId?: string | null;
  onTodoClick: (todo: Todo, modalContext?: {
    previousModal?: string;
    previousPhase?: string;
    previousState?: Record<string, unknown>;
  }) => void;
  onTodoStatusChange: (todoId: string, status: TaskStatus) => void;
  onAddTodo: () => void;
  onAddTodayTask: () => void;
  onAddWeekTask: () => void;
  onAddChild: (parentId: string) => void;
  onMakeChild: (childId: string, parentId: string) => void;
  onStartPomodoro?: (todo: Todo) => void;
  // Removed onToggleActiveWeek - now using date-based filtering
  onDelete?: (todoId: string) => void;
  teamMembers: Array<{ id: string; name: string; email: string }>;
  isPersonalMode: boolean;
  todos: Todo[];
  statusFilter: TaskStatus[];
  onStatusFilterChange: (statuses: TaskStatus[]) => void;
  // Search and filter props
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  onStatusToggle: (status: TaskStatus) => void;
  horizonFilter: string[];
  onHorizonToggle: (horizon: string) => void;
  tagsFilter: string[];
  onTagToggle: (tag: string) => void;
  taskTypeFilter: 'all' | 'parent_only' | 'child_only';
  onTaskTypeChange: (type: 'all' | 'parent_only' | 'child_only') => void;
  allTags: string[];
  onClearFilters: () => void;
  allTodos: Todo[];
  onCreateTodo?: (todo: TodoCreate) => Promise<Todo>;
  onUpdateTodo?: (todoId: string, updates: TodoUpdate) => Promise<void>;
}

const WeekView: React.FC<WeekViewProps> = ({
  selectedTeamId,
  onTodoClick,
  onTodoStatusChange,
  onAddTodo,
  onAddTodayTask,
  onAddWeekTask,
  onAddChild,
  onMakeChild,
  onStartPomodoro,
  onDelete,
  teamMembers,
  isPersonalMode,
  todos,
  statusFilter,
  onStatusFilterChange,
  // Search and filter props
  searchQuery,
  onSearchChange,
  showFilters,
  onToggleFilters,
  onStatusToggle,
  horizonFilter,
  onHorizonToggle,
  tagsFilter,
  onTagToggle,
  taskTypeFilter,
  onTaskTypeChange,
  allTags,
  onClearFilters,
  allTodos,
}) => {
  const [currentWeek, setCurrentWeek] = useState<Week | null>(null);
  
  // Determine if showing incomplete tasks only
  const isIncompleteOnly = statusFilter.length > 0 && !statusFilter.includes('completed');

  // Inbox count for review integration
  const inboxCount = useMemo(() => getInboxTasks(allTodos).length, [allTodos]);

  // Memoize today's date string to avoid recalculating on every render
  const todayDateStr = useMemo(() => {
    const today = new Date();
    return today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');
  }, []);

  // Memoize today's tasks - horizon=NOW
  const todayTasks = useMemo(() => {
    return todos.filter(todo => {
      if (isIncompleteOnly && (todo.status === 'completed' || todo.status === 'canceled')) return false;
      return todo.horizon === 'now';
    });
  }, [todos, isIncompleteOnly]);

  // Memoize active week tasks - horizon=NEXT
  const activeWeekTasks = useMemo(() => {
    return todos.filter(todo => {
      if (isIncompleteOnly && (todo.status === 'completed' || todo.status === 'canceled')) return false;
      return todo.horizon === 'next';
    });
  }, [todos, isIncompleteOnly]);

  // Fetch today's events (reuse memoized todayDateStr)
  const { data: todayEvents = [] } = useQuery({
    queryKey: ['events', 'date', todayDateStr],
    queryFn: () => eventsAPI.getByDate(todayDateStr),
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTodayExpanded, setIsTodayExpanded] = useState(true);
  const [isWeekTasksExpanded, setIsWeekTasksExpanded] = useState(true);

  // Handle toggle between all tasks and incomplete only
  const handleTaskFilterToggle = () => {
    if (isIncompleteOnly) {
      // Switch to show all tasks (clear all status filters)
      onStatusFilterChange([]);
    } else {
      // Switch to show incomplete only (check all non-completed statuses)
      const incompleteStatuses: TaskStatus[] = [
        'not_started',
        'in_progress',
        'waiting'
      ];
      onStatusFilterChange(incompleteStatuses);
    }
  };
  const [showSidebar, setShowSidebar] = useState(true);
  const [mobileView, setMobileView] = useState<'tasks' | 'summary'>('tasks');
  const [showWeeklyReview, setShowWeeklyReview] = useState(false);

  // Check for pending review from widget navigation
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingReview');
    if (pending === 'weekly') {
      sessionStorage.removeItem('pendingReview');
      setShowWeeklyReview(true);
    }
  }, []);

  // Listen for reopen weekly review events
  useEffect(() => {
    const handleReopenWeeklyReview = () => {
      setShowWeeklyReview(true);
    };

    window.addEventListener('reopenWeeklyReview', handleReopenWeeklyReview as EventListener);

    return () => {
      window.removeEventListener('reopenWeeklyReview', handleReopenWeeklyReview as EventListener);
    };
  }, []);
  const [isDraggingOverToday, setIsDraggingOverToday] = useState(false);
  const [isDraggingOverWeek, setIsDraggingOverWeek] = useState(false);
  const [hoveredAddButton, setHoveredAddButton] = useState<string | null>(null);
  const [draggedTodoId, setDraggedTodoId] = useState<string | null>(null);
  
  const canMoveTodo = (todoId: string) => {
    const todo = todos.find(t => t.id === todoId);
    if (!todo || !todo.deadline) return true;

    const dueDate = new Date(todo.deadline);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    return dueDate > today;
  };
  
  const { showNotification } = useNotification();

  // Previous week data for review context
  const [previousWeekData, setPreviousWeekData] = useState<{
    goals?: string[];
    reflections?: import('../types').ReviewEntry[];
    completedTaskCount?: number;
  } | null>(null);
  const [isLoadingPreviousWeek, setIsLoadingPreviousWeek] = useState(false);

  const loadPreviousWeekData = useCallback(async () => {
    if (!currentWeek) return;
    try {
      setIsLoadingPreviousWeek(true);
      const currentWeekDate = new Date(currentWeek.start_date);
      const previousWeekDate = new Date(currentWeekDate);
      previousWeekDate.setDate(previousWeekDate.getDate() - 7);

      const weeks = await weeksAPI.getAll(undefined, 10);
      const prevWeek = weeks.find((w: Week) => {
        const weekStart = new Date(w.start_date);
        return Math.abs(weekStart.getTime() - previousWeekDate.getTime()) < 86400000;
      });

      if (prevWeek) {
        const tasksResponse = await weeksAPI.getTasks(prevWeek.id) as { completed_tasks?: Todo[] };
        setPreviousWeekData({
          goals: prevWeek.goals,
          reflections: prevWeek.reflections,
          completedTaskCount: tasksResponse.completed_tasks?.length ?? 0,
        });
      } else {
        setPreviousWeekData(null);
      }
    } catch (error) {
      console.error('Failed to load previous week data:', error);
      setPreviousWeekData(null);
    } finally {
      setIsLoadingPreviousWeek(false);
    }
  }, [currentWeek]);

  // Load previous week data when review opens
  useEffect(() => {
    if (showWeeklyReview && currentWeek) {
      loadPreviousWeekData();
    }
  }, [showWeeklyReview, currentWeek, loadPreviousWeekData]);

  useEffect(() => {
    loadCurrentWeek();
  }, [selectedTeamId]);

  // Note: Removed aggressive useEffect that was causing race conditions
  // The query invalidation in useTodos should handle refreshing automatically

  const loadCurrentWeek = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get current week metadata only
      const week = await weeksService.getCurrentWeek(selectedTeamId || undefined);
      setCurrentWeek(week);

    } catch (err) {
      setError('Failed to load week data');
      console.error('Error loading week:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatWeekRange = (week: Week) => {
    const start = new Date(week.start_date);
    const end = new Date(week.end_date);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const handleAddChild = (parentId: string) => {
    onAddChild(parentId);
  };

  const handleMakeChild = (todoId: string, parentId: string) => {
    onMakeChild(todoId, parentId);
  };

  const handleUpdateWeek = async (updates: Partial<Week>) => {
    if (!currentWeek) return;
    
    try {
      await weeksService.updateWeek(currentWeek.id, updates);
      await loadCurrentWeek(); // Reload week data
      showNotification('success', 'Week updated successfully!');
    } catch (error) {
      console.error('Failed to update week:', error);
      showNotification('error', 'Failed to update week. Please try again.');
    }
  };

  const handleMarkReviewComplete = async () => {
    if (!currentWeek) return;
    
    try {
      await weeksService.completeWeeklyReview(currentWeek.id);
      await loadCurrentWeek(); // Reload week data
      showNotification('success', 'Weekly review completed successfully!');
    } catch (error) {
      console.error('Failed to complete review:', error);
      showNotification('error', 'Failed to complete review. Please try again.');
    }
  };

  const handleDragToToday = async (todoId: string) => {
    try {
      // TODO: Fix this - onTodoStatusChange expects TaskStatus, not update object
      await (onTodoStatusChange as unknown as (id: string, updates: Record<string, unknown>) => Promise<void>)(todoId, {
        horizon: 'now'
      });

      // Force reload after update
      await loadCurrentWeek();
      showNotification('success', 'Task moved to Today\'s Focus!');
    } catch (error) {
      console.error('Failed to move task to today:', error);
      showNotification('error', 'Failed to move task. Please try again.');
    }
  };

  const handleDragToWeek = async (todoId: string) => {
    try {
      // TODO: Fix this - onTodoStatusChange expects TaskStatus, not update object
      await (onTodoStatusChange as unknown as (id: string, updates: Record<string, unknown>) => Promise<void>)(todoId, {
        horizon: 'next'
      });

      // Force reload after update
      await loadCurrentWeek();
      showNotification('success', 'Task added to Active This Week!');
    } catch (error) {
      console.error('Failed to add task to week:', error);
      showNotification('error', 'Failed to add task. Please try again.');
    }
  };

  const handleTodayDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverToday(false);
    const todoId = e.dataTransfer.getData('text/plain');
    if (todoId) {
      handleDragToToday(todoId);
    }
  };

  const handleTodayDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsDraggingOverToday(true);
  };

  const handleTodayDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingOverToday(false);
    }
  };

  const handleWeekDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOverWeek(false);
    setDraggedTodoId(null);
    const todoId = e.dataTransfer.getData('text/plain');
    if (todoId) {
      handleDragToWeek(todoId);
    }
  };

  const handleWeekDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const todoId = e.dataTransfer.getData('text/plain');
    
    if (!canMoveTodo(todoId)) {
      e.dataTransfer.dropEffect = 'none';
    } else {
      e.dataTransfer.dropEffect = 'move';
    }
    
    setIsDraggingOverWeek(true);
    setDraggedTodoId(todoId);
  };

  const handleWeekDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingOverWeek(false);
      setDraggedTodoId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          type="button"
          onClick={loadCurrentWeek}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!currentWeek) {
    return null;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Week Header - Always visible */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div className="flex items-center justify-between sm:block">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-500 font-medium">
                    Week of:
                  </p>
                  <p className="text-lg text-gray-600 dark:text-gray-400">
                    {formatWeekRange(currentWeek)}
                  </p>
                </div>
                
                {/* Mobile View Toggle - Switch between Tasks and Summary */}
                <button
                  type="button"
                  onClick={() => setMobileView(mobileView === 'tasks' ? 'summary' : 'tasks')}
                  className={`sm:hidden flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    mobileView === 'summary'
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title={mobileView === 'tasks' ? 'Switch to week summary view' : 'Switch to tasks view'}
                >
                  <Sidebar className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {mobileView === 'tasks' ? 'Summary' : 'Tasks'}
                  </span>
                </button>
              </div>
              
              <div className="flex items-center space-x-3 sm:space-x-6 flex-1">
                {/* Search and Filter Bar */}
                <div className="flex-1 sm:max-w-xl">
                  <SearchFilterBar
                    searchQuery={searchQuery}
                    onSearchChange={onSearchChange}
                    showFilters={showFilters}
                    onToggleFilters={onToggleFilters}
                    statusFilter={statusFilter}
                    onStatusToggle={onStatusToggle}
                    horizonFilter={horizonFilter}
                    onHorizonToggle={onHorizonToggle}
                    tagsFilter={tagsFilter}
                    onTagToggle={onTagToggle}
                    taskTypeFilter={taskTypeFilter}
                    onTaskTypeChange={onTaskTypeChange}
                    allTags={allTags}
                    onClearFilters={onClearFilters}
                  />
                </div>
                
                {/* Sidebar Toggle - Desktop only */}
                <button
                  type="button"
                  onClick={() => setShowSidebar(!showSidebar)}
                  className="hidden sm:flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={showSidebar ? 'Hide week summary and goals' : 'Show week summary and goals'}
                >
                  <Sidebar className="w-4 h-4" />
                  <span className="hidden sm:inline lg:hidden xl:inline">
                    {showSidebar ? 'Hide Summary' : 'Week Summary'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
        {/* Mobile Summary View - Only visible on mobile when summary is selected */}
        {mobileView === 'summary' && (
          <div className="lg:hidden flex-1 overflow-y-auto">
            <WeekSummary
              week={currentWeek}
              todayTasks={todayTasks}
              activeWeekTasks={activeWeekTasks}
              draftTasks={[]}
              onStartWeeklyReview={() => setShowWeeklyReview(true)}
            />
          </div>
        )}

        {/* Main Content (Tasks) - Shows on desktop always, on mobile only when tasks view is selected */}
        <div className={`flex-1 overflow-y-auto min-h-0 ${mobileView === 'tasks' ? 'block' : 'hidden lg:block'}`}>
          <div className="max-w-7xl mx-auto space-y-4 px-4 sm:px-6 lg:px-8 py-4">

        <div className="space-y-4">
          {/* Today's Tasks */}
          <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 ${
            isDraggingOverToday ? 'bg-blue-50 border-blue-400 shadow-lg' : ''
          }`}
            onDrop={handleTodayDrop}
            onDragOver={handleTodayDragOver}
            onDragLeave={handleTodayDragLeave}
          >
            <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg">
              <button
                type="button"
                onClick={() => setIsTodayExpanded(!isTodayExpanded)}
                className="flex-1 text-left"
                title="Tasks with due date or todo date of today"
              >
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  {isTodayExpanded ? (
                    <ChevronDown className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                  )}
                  <Clock className="w-5 h-5 mr-2 text-blue-600" />
                  Today's Focus
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({todayTasks.length})
                  </span>
                  {isDraggingOverToday && (
                    <span className="ml-2 text-sm font-medium text-blue-600 animate-pulse">
                      Drop here to set as today's task
                    </span>
                  )}
                </h2>
              </button>
              
              {/* Task Filter Toggle */}
              <div className="flex items-center space-x-2 ml-4">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {isIncompleteOnly ? 'Incomplete Only' : 'All Tasks'}
                </span>
                <button
                  type="button"
                  onClick={handleTaskFilterToggle}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    isIncompleteOnly
                      ? 'bg-blue-600' 
                      : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                  title={isIncompleteOnly ? 'Show all tasks' : 'Show incomplete tasks only'}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      isIncompleteOnly ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <button
                type="button"
                onClick={onAddTodayTask}
                onMouseEnter={() => setHoveredAddButton('today')}
                onMouseLeave={() => setHoveredAddButton(null)}
                className="ml-3 p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
                title="Add new task for today's focus"
              >
                {hoveredAddButton === 'today' ? (
                  <X className="w-4 h-4 transform rotate-45 transition-transform duration-200" />
                ) : (
                  <Plus className="w-4 h-4 transition-transform duration-200" />
                )}
              </button>
            </div>
            
            {isTodayExpanded && (
              <div className="px-3 pb-3">
                <div className="border-b border-gray-200 dark:border-gray-700 mb-4"></div>

                {/* Today's Events */}
                {todayEvents.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-2">Events</h3>
                    <div className="space-y-1">
                      {todayEvents.map(event => (
                        <div
                          key={event.id}
                          className="flex items-center gap-2 p-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg text-sm"
                        >
                          <Calendar className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                          <span className="font-medium text-indigo-900 dark:text-indigo-200 truncate">{event.title}</span>
                          {event.start_time && (
                            <span className="ml-auto text-xs text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                              {parseLocalTimestamp(event.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {event.end_time && ` - ${parseLocalTimestamp(event.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {todayTasks.length === 0 && todayEvents.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No tasks or events scheduled for today</p>
                    <button type="button" onClick={onAddTodayTask} className="btn-primary">
                      Add Today's Task
                    </button>
                  </div>
                ) : todayTasks.length === 0 ? null : (
                  <div className="space-y-2">
                    {todayTasks.map(todo => (
                      <TodoItem
                        key={todo.id}
                        todo={todo}
                        onClick={() => onTodoClick(todo)}
                        onStatusChange={(status) => onTodoStatusChange(todo.id, status)}
                        onSubtaskStatusChange={onTodoStatusChange}
                        onAddChild={handleAddChild}
                        onSubtaskClick={onTodoClick}
                        onMakeChild={handleMakeChild}
                        onStartPomodoro={onStartPomodoro}
                        onDelete={onDelete}
                        allTodos={allTodos}
                        teamMembers={teamMembers}
                        isPersonalMode={isPersonalMode}
                        isDraggable={true}
                        hideActiveWeekIndicator={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Week Tasks */}
          <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 ${
            isDraggingOverWeek ? 'bg-green-50 border-green-400 shadow-lg' : ''
          }`}
            onDrop={handleWeekDrop}
            onDragOver={handleWeekDragOver}
            onDragLeave={handleWeekDragLeave}
          >
            <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg">
              <button
                type="button"
                onClick={() => setIsWeekTasksExpanded(!isWeekTasksExpanded)}
                className="flex-1 text-left"
                title="Tasks marked as active for this week - drag tasks here to add them"
              >
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  {isWeekTasksExpanded ? (
                    <ChevronDown className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                  )}
                  <Calendar className="w-5 h-5 mr-2 text-green-600" />
                  This Week
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({activeWeekTasks.length})
                  </span>
                  {isDraggingOverWeek && (
                    <span className={`ml-2 text-sm font-medium animate-pulse ${
                      draggedTodoId && !canMoveTodo(draggedTodoId)
                        ? 'text-red-600'
                        : 'text-green-600'
                    }`}>
                      {draggedTodoId && !canMoveTodo(draggedTodoId)
                        ? 'Cannot move - has deadline/todo date today/past'
                        : 'Drop here to add to this week'
                      }
                    </span>
                  )}
                </h2>
              </button>
              
              {/* Task Filter Toggle */}
              <div className="flex items-center space-x-2 ml-4">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {isIncompleteOnly ? 'Incomplete Only' : 'All Tasks'}
                </span>
                <button
                  type="button"
                  onClick={handleTaskFilterToggle}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    isIncompleteOnly
                      ? 'bg-blue-600' 
                      : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                  title={isIncompleteOnly ? 'Show all tasks' : 'Show incomplete tasks only'}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      isIncompleteOnly ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <button
                type="button"
                onClick={onAddWeekTask}
                onMouseEnter={() => setHoveredAddButton('week')}
                onMouseLeave={() => setHoveredAddButton(null)}
                className="ml-3 p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-all duration-200"
                title="Add new task for this week"
              >
                {hoveredAddButton === 'week' ? (
                  <X className="w-4 h-4 transform rotate-45 transition-transform duration-200" />
                ) : (
                  <Plus className="w-4 h-4 transition-transform duration-200" />
                )}
              </button>
            </div>
            
            {isWeekTasksExpanded && (
              <div className="px-3 pb-3">
                <div className="border-b border-gray-200 dark:border-gray-700 mb-4"></div>
                {activeWeekTasks.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No active tasks for this week</p>
                    <button type="button" onClick={() => setShowWeeklyReview(true)} className="btn-primary">
                      Plan Your Week
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {activeWeekTasks.filter(task => !todayTasks.some(t => t.id === task.id)).map(todo => (
                      <TodoItem
                        key={todo.id}
                        todo={todo}
                        onClick={() => onTodoClick(todo)}
                        onStatusChange={(status) => onTodoStatusChange(todo.id, status)}
                        onSubtaskStatusChange={onTodoStatusChange}
                        onAddChild={handleAddChild}
                        onSubtaskClick={onTodoClick}
                        onMakeChild={handleMakeChild}
                        onStartPomodoro={onStartPomodoro}
                        onDelete={onDelete}
                        allTodos={allTodos}
                        teamMembers={teamMembers}
                        isPersonalMode={isPersonalMode}
                        isDraggable={true}
                        hideActiveWeekIndicator={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>

          {/* Quick Add Button */}
          <div className="fixed bottom-6 right-6">
            <button
              type="button"
              onClick={onAddTodo}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-colors"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

        {/* Week Summary Sidebar - Desktop only */}
        {showSidebar && (
          <div className="hidden lg:block lg:w-96 lg:border-l border-gray-200 dark:border-gray-700 lg:h-screen overflow-y-auto flex-shrink-0">
            <WeekSummary
              week={currentWeek}
              todayTasks={todayTasks}
              activeWeekTasks={activeWeekTasks}
              draftTasks={[]}
              onStartWeeklyReview={() => setShowWeeklyReview(true)}
            />
          </div>
        )}
      </div>

      {/* Weekly Review Modal */}
      {currentWeek && (
        <ReviewModal
          isOpen={showWeeklyReview}
          onClose={() => setShowWeeklyReview(false)}
          reviewType="weekly"
          periodLabel={`Week ${currentWeek.week_number}, ${currentWeek.year}`}
          reflections={currentWeek.reflections}
          goals={currentWeek.goals}
          previousPeriod={previousWeekData}
          isLoadingPreviousData={isLoadingPreviousWeek}
          inboxCount={inboxCount}
          onSave={(data) => handleUpdateWeek({ ...data, is_review_completed: false })}
          onComplete={(data) => {
            handleUpdateWeek({ ...data, is_review_completed: true });
            handleMarkReviewComplete();
          }}
        />
      )}
    </div>
  );
};

export default WeekView;